import { useIdentify, type Customer } from '@hungryroot/customer';
import { Flavors } from '@hungryroot/icons';
import { FoodProfileDisclosure } from '@hungryroot/page-food-profile';
import { Paragraph, Heading } from '@hungryroot/ramen';
import { tagGroups } from '@hungryroot/tags';
import {
  useCustomerTagGroup,
  type CustomerTagGroupResponse,
} from '@hungryroot/tags';
import {
  useTagGroup,
  type Tag,
} from '../../../../api/v2/tag_groups/get/v2-tag-groups-get';
import { type SpicyLevel } from '../../../../components/SpicyButtons/spice-level';
import { ControlSpicyPopover } from '../ControlSpicyPopover/ControlSpicyPopover';
import { ControlTagBinary } from '../ControlTagBinary/ControlTagBinary';
import styles from './FlavorsSection.module.css';

interface Props {
  testid?: string;
  customerId: Customer['id'];
}

enum TagPreference {
  Like = 1,
  NoRating = 0,
  Dislike = -1,
}

function getSpicyPref(customerSpicyLevel: SpicyLevel | null | undefined) {
  switch (customerSpicyLevel) {
    case 0:
      return {
        title: 'No heat',
        subtitle: 'ex: red bell pepper',
      };
    case 1:
      return {
        title: 'Mild',
        subtitle: 'ex: pepperoncini pepper',
      };
    case 2:
      return {
        title: 'Medium',
        subtitle: 'ex: jalapeno pepper',
      };
    case 3:
      return {
        title: 'Hot',
        subtitle: 'ex: cayenne pepper',
      };
    default:
      return null;
  }
}

function getSubtitle(
  queryTags: Tag[] | undefined,
  customerTags: CustomerTagGroupResponse | undefined,
  customerSpicyLevel: SpicyLevel | undefined | null
) {
  if (!customerTags && !queryTags && !customerSpicyLevel) {
    return;
  }
  const selectedTagDetails = queryTags?.reduce((acc: Tag[], cur: Tag) => {
    return customerTags?.results.some(
      (obj) => obj.tag === cur.id && obj.preference === TagPreference.Like
    )
      ? [...acc, cur]
      : [...acc];
  }, []);
  const tagNames = selectedTagDetails?.map((tag) => tag.name);

  const spicyPreference = getSpicyPref(customerSpicyLevel);
  if (spicyPreference) {
    tagNames?.push(spicyPreference.title);
  }

  return tagNames?.join(', ');
}

function getHeaderLinkText(
  customerTags: CustomerTagGroupResponse | undefined,
  customerSpicyLevel: SpicyLevel | undefined | null
) {
  const hasRatedTags = customerTags?.results.find(
    (tag) => tag.preference === TagPreference.Like
  );
  if (hasRatedTags && !customerSpicyLevel) {
    return 'Set your heat tolerance';
  } else if (!hasRatedTags && customerSpicyLevel) {
    return 'Select more flavors you like';
  }

  return;
}

export function FlavorsSection(props: Props) {
  const { testid, customerId } = props;
  const { data: customerTagGroupData } = useCustomerTagGroup(tagGroups.flavor);
  const tagGroup = useTagGroup(tagGroups.flavor);
  const { data: tagGroupData } = tagGroup || {};
  const queryTags = tagGroupData?.tags;
  const { customerData } = useIdentify();
  const customerSpicyLevel = customerData?.spicy_level;

  const subtitle = getSubtitle(
    queryTags,
    customerTagGroupData,
    customerSpicyLevel
  );
  const headerLinkText = getHeaderLinkText(
    customerTagGroupData,
    customerSpicyLevel
  );

  return (
    <section data-testid={testid} className={styles.container}>
      <FoodProfileDisclosure
        testid={`${testid}-disclosure`}
        title="Flavors"
        subtitle={subtitle || 'Set your preferences.'}
        icon={<Flavors />}
        deepLink="flavor-preferences"
        headerLinkText={headerLinkText}
      >
        <Heading variant="H5">What flavors do you like?</Heading>
        <Paragraph className={styles.text}>
          We'll use your preferences to suggest recipes that suit your tastes.
        </Paragraph>
        <ControlTagBinary
          testid={`${testid}-control-flavor`}
          customerId={customerId}
          tagGroupId={tagGroups.flavor}
          showSubtitle={true}
        />
        <div id="#spicy" className={styles.spicy}>
          <Heading className={styles['spicy-title']} variant="H5">
            How much heat do you prefer in your food?
          </Heading>
          <ControlSpicyPopover testid={`${testid}-control-spicy`} />
        </div>
      </FoodProfileDisclosure>
    </section>
  );
}
